import { computed, ref, type ComputedRef, type Ref } from 'vue'

import type { ProductUpdate, RoadmapItem, WhatsNewLoadState } from '../models/whatsNew.types'
import { useWhatsNewStore } from '../stores/whatsNewStore'

import { useAccount, useWorkspace } from '@/features/account'

export interface UseWhatsNew {
  updates: ComputedRef<ProductUpdate[]>
  roadmap: ComputedRef<RoadmapItem[]>
  myVotes: ComputedRef<Set<number>>
  hasUnseen: ComputedRef<boolean>
  state: ComputedRef<WhatsNewLoadState>
  isOpen: Ref<boolean>
  open: () => void
  close: () => void
  load: () => Promise<void>
  retry: () => Promise<void>
  vote: (item: RoadmapItem) => Promise<void>
}

export function useWhatsNew(): UseWhatsNew {
  const store = useWhatsNewStore()
  const account = useAccount()
  const workspace = useWorkspace()
  const isOpen = ref(false)
  const localVotes = ref<Set<number> | null>(null)
  const myVotes = computed(() => {
    if (localVotes.value) return localVotes.value
    const value = workspace.getAttribute('product_roadmap_votes')?.value
    return new Set(Array.isArray(value) ? value.map(Number).filter(Number.isFinite) : [])
  })

  function open(): void {
    isOpen.value = true
    store.markSeen()
    void store.loadContent()
  }

  function close(): void {
    isOpen.value = false
  }

  async function retry(): Promise<void> {
    await store.loadContent(true)
  }

  async function load(): Promise<void> {
    await store.loadContent()
  }

  async function vote(item: RoadmapItem): Promise<void> {
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    if (accountId === undefined || workspaceId === undefined) return
    const previous = new Set(myVotes.value)
    const next = new Set(previous)
    const removing = next.has(item.id)
    if (removing) next.delete(item.id)
    else next.add(item.id)
    localVotes.value = next
    try {
      await store.toggleVote(accountId, Number(workspaceId), item.id, removing)
      await workspace.refreshAttributes()
    } catch {
      localVotes.value = previous
    }
  }

  return {
    updates: computed(() => store.sortedUpdates),
    roadmap: computed(() => store.roadmap),
    myVotes,
    hasUnseen: computed(() => store.hasUnseen),
    state: computed(() => store.state),
    isOpen,
    open,
    close,
    load,
    retry,
    vote,
  }
}
